/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { KeyboardEvent } from "react";

const FORM_FOCUSABLE_SELECTOR = [
  'input:not([type="hidden"])',
  'textarea',
  'select',
  '[role="combobox"]',
].join(',');

const SUBMIT_TRIGGER_SELECTOR = '[data-form-submit="true"]';

/**
 * Determines whether a form element should be ignored for focus navigation.
 *
 * @param element - Element to evaluate.
 * @returns True when element is hidden or disabled.
 */
const isHiddenOrDisabled = (element: HTMLElement): boolean => {
  if (element.getAttribute('aria-hidden') === 'true') return true;
  if ((element as HTMLInputElement).disabled) return true;
  if (element.getAttribute('tabindex') === '-1') return true;
  const style = window.getComputedStyle(element);
  return style.display === 'none' || style.visibility === 'hidden';
};

/**
 * Collects all focusable form fields within a form element.
 *
 * @param form - Form root element.
 * @returns Focusable field elements.
 */
const getFocusableFormFields = (form: HTMLElement): HTMLElement[] => {
  const elements = Array.from(
    form.querySelectorAll<HTMLElement>(FORM_FOCUSABLE_SELECTOR)
  );
  return elements.filter((element) => !isHiddenOrDisabled(element));
};

/**
 * Checks if an element is a combobox input.
 *
 * @param element - Element to evaluate.
 * @returns True if element is a combobox.
 */
const isCombobox = (element: HTMLElement): boolean => {
  return element.getAttribute('role') === 'combobox' || element.matches('[role="combobox"]');
};

/**
 * Focuses a field and opens combobox dropdowns.
 *
 * @param element - Target field element.
 * @returns Void.
 */
const focusField = (element: HTMLElement): void => {
  element.focus();
  if (isCombobox(element)) {
    element.click();
  }
};

/**
 * Focuses the next focusable field in the form.
 *
 * @param form - Form root element.
 * @param currentTarget - Currently focused element.
 * @returns Void.
 */
export const focusNextField = (form: HTMLElement, currentTarget: HTMLElement): void => {
  const fields = getFocusableFormFields(form);
  const currentIndex = fields.findIndex(
    (field) => field === currentTarget || field.contains(currentTarget)
  );

  if (currentIndex === -1) return;
  const nextField = fields[currentIndex + 1];

  if (!nextField) {
    const submitTarget = form.querySelector<HTMLElement>(SUBMIT_TRIGGER_SELECTOR);
    submitTarget?.click();
    return;
  }

  focusField(nextField);
};

/**
 * Checks if any modifier key (Shift, Alt, Ctrl, Meta) is pressed during a keyboard event.
 * @param event - The keyboard event to check
 * @returns True if any modifier key is held
 */
const hasModifierKey = (event: KeyboardEvent<HTMLElement>): boolean =>
  event.shiftKey || event.altKey || event.ctrlKey || event.metaKey;

/**
 * Checks if the target is a textarea or does not match the focusable selector.
 * @param target - The element to check
 * @returns True if the target should not receive focus
 */
const isNonFocusableTarget = (target: HTMLElement): boolean =>
  target instanceof HTMLTextAreaElement || !target.matches(FORM_FOCUSABLE_SELECTOR);

/**
 * Determines whether a keydown event should be ignored (non-Enter, modifier keys, or non-focusable targets).
 * @param event - The keyboard event to evaluate
 * @returns True if the event should be ignored
 */
export const shouldIgnoreKeyDown = (event: KeyboardEvent<HTMLElement>): boolean => {
  if (event.defaultPrevented || event.key !== 'Enter' || hasModifierKey(event)) return true;

  const target = event.target as HTMLElement | null;
  return !target || isNonFocusableTarget(target);
};

/**
 * Handles focus transition for an expanded combobox, deferring to the next field after selection.
 * @param form - The parent form element
 * @param target - The currently focused combobox element
 * @returns True if the combobox was expanded and focus transition was scheduled
 */
export const handleExpandedCombobox = (form: HTMLElement, target: HTMLElement): boolean => {
  const isExpanded = isCombobox(target) && target.getAttribute('aria-expanded') === 'true';
  if (isExpanded) {
    setTimeout(() => focusNextField(form, target), 0);
  }
  return isExpanded;
};
